import {isUserPremium, isUserUltra} from "services/FirebaseDbService.js";
import {isProUser} from "services/UserService.js";

/**
 * Entitlement Service
 * Checks if user has pro or plus entitlements based on subscription data
 * Uses new subscription structure (premium/ultra)
 */

/** App user wrapper shape consumed by the entitlement helpers. */
export interface AppUserLike {
  userInDb?: any;
  [key: string]: any;
}

/** Tiers in descending order of precedence, matching how the UI picks what to show. */
const TIERS = ["ultra", "premium"];

/** Stores billing our own web subscriptions: Stripe for older ones, Paddle since the move. */
export const WEB_BILLED_STORES = ["stripe", "paddle"];

export const isWebBilledStore = (store: unknown): boolean =>
  typeof store === "string" && WEB_BILLED_STORES.includes(store);

/**
 * Stores we can manage ourselves (our web billing, plus "adapty" = an unbilled
 * grant), so they never block a web purchase. Deliberately an allowlist: Adapty's
 * store spellings are only partly confirmed, and an unrecognised value should
 * block rather than risk a double charge.
 */
export const SELF_MANAGED_STORES = [...WEB_BILLED_STORES, "adapty"];

/** Whether a tier node is currently granting access. */
export const isTierActive = (tier: any): boolean =>
  typeof tier?.expiration === "number" && tier.expiration * 1000 >= Date.now();

/**
 * The active third-party-billed subscription that blocks a web purchase, or null.
 * A tier with no store recorded does not block — legacy records predate the field.
 */
export const getBlockingStoreSubscription = (appUser: AppUserLike): {tier: string; store: string} | null => {
  const subscription = appUser?.userInDb?.subscription;
  if (!subscription) {
    return null;
  }
  for (const tier of TIERS) {
    const node = subscription[tier];
    if (isTierActive(node) && node?.store && !SELF_MANAGED_STORES.includes(node.store)) {
      return {tier, store: node.store};
    }
  }
  return null;
};

/**
 * Tiers currently granting access, highest first. A user holds at most one web
 * subscription, yet after a plan change Adapty keeps the old access level alive
 * until it lapses, so a web-billed tier under a higher web-billed one is that same
 * subscription and is left out.
 */
export const getActiveSubscriptionTiers = (appUser: AppUserLike): string[] => {
  const subscription = appUser?.userInDb?.subscription;
  if (!subscription) {
    return [];
  }
  const active = TIERS.filter((tier) => isTierActive(subscription[tier]));
  const webTier = active.find((tier) => isWebBilledStore(subscription[tier]?.store));
  return active.filter((tier) => !isWebBilledStore(subscription[tier]?.store) || tier === webTier);
};

/**
 * The tier the user's live web (Stripe or Paddle) subscription grants, or null.
 * Resolved by store, not by highest active tier: a granted Ultra can sit above a
 * paid Pro, and the paid one is what cancel/switch must act on.
 */
export const getWebSubscriptionTier = (appUser: AppUserLike): string | null => {
  const subscription = appUser?.userInDb?.subscription;
  if (!subscription) {
    return null;
  }
  return TIERS.find((tier) => isTierActive(subscription[tier]) && isWebBilledStore(subscription[tier]?.store)) || null;
};

/**
 * Whether the user has ever held a subscription, on any store. Any recorded
 * expiration counts, lapsed or not: the free trial is first-time-only (enforced
 * server-side), so the paywall copy must not promise one to a returning subscriber.
 */
export const hasSubscribedBefore = (appUser: AppUserLike): boolean => {
  const userInDb = appUser?.userInDb;
  return Boolean(
    userInDb?.subscription?.premium?.expiration ||
    userInDb?.subscription?.ultra?.expiration ||
    userInDb?.subscriptionExpDate
  );
};

/**
 * Check if user has a specific entitlement (pro or plus)
 * @param {Object} appUser - The app user object
 * @param {string} entitlementType - 'pro' or 'ultra'
 * @return {boolean} - True if user has the entitlement and it's not expired
 */
export const hasEntitlement = (appUser: AppUserLike, entitlementType: string = "pro"): boolean => {
  if (!appUser || !appUser.userInDb) {
    return false;
  }

  const user = appUser.userInDb;

  if (entitlementType === "pro") {
    // Delegated so this cannot disagree with `appUser.isUserPro`, which the profile
    // screens report. They did, and paying users lost Pro features because of it.
    return isProUser(user);
  } else if (entitlementType === "ultra") {
    // No legacy fallback: `subscriptionExpDate` carries no tier.
    if (!user.subscription) {
      return false;
    }
    return isUserUltra(user).isActive;
  }

  return false;
};

/**
 * Check if user has pro entitlement
 * @param {Object} appUser - The app user object
 * @return {boolean} - True if user has pro entitlement
 */
export const hasProEntitlement = (appUser: AppUserLike): boolean => {
  return hasEntitlement(appUser, "pro");
};

/**
 * Check if user has plus entitlement
 * @param {Object} appUser - The app user object
 * @return {boolean} - True if user has plus entitlement
 */
export const hasUltraEntitlement = (appUser: AppUserLike): boolean => {
  return hasEntitlement(appUser, "ultra");
};

/**
 * Get entitlement expiration date
 * @param {Object} appUser - The app user object
 * @param {string} entitlementType - 'pro' or 'plus'
 * @return {Date|null} - Expiration date or null if not found
 */
export const getEntitlementExpirationDate = (appUser: AppUserLike, entitlementType: string = "pro"): Date | null => {
  if (!appUser || !appUser.userInDb) {
    return null;
  }

  const user = appUser.userInDb;

  if (!user.subscription) {
    return null;
  }

  const premiumStatus = isUserPremium(user);
  const ultraStatus = isUserUltra(user);

  if (entitlementType === "pro") {
    // Pro = use the later expiration date between premium and ultra
    if (premiumStatus.isActive && ultraStatus.isActive) {
      const premiumDate = new Date(premiumStatus.expirationDate * 1000);
      const ultraDate = new Date(ultraStatus.expirationDate * 1000);
      return premiumDate.getTime() > ultraDate.getTime() ? premiumDate : ultraDate;
    } else if (ultraStatus.isActive) {
      return new Date(ultraStatus.expirationDate * 1000);
    } else if (premiumStatus.isActive) {
      return new Date(premiumStatus.expirationDate * 1000);
    }
  } else if (entitlementType === "ultra") {
    // Plus = ultra subscription expiration
    if (ultraStatus.isActive) {
      return new Date(ultraStatus.expirationDate * 1000);
    }
  }

  return null;
};

/**
 * Get Plus entitlement expiration date (includes sandbox users)
 * @param {Object} appUser - The app user object
 * @return {Date|null} - Expiration date or null if not found
 */
export const getPlusEntitlementExpirationDate = (appUser: AppUserLike): Date | null => {
  if (!appUser || !appUser.userInDb) {
    return null;
  }

  const user = appUser.userInDb;

  // Get sandbox expiration date if applicable
  const subscriptionType = user.subscription?.type || user.subscriptionType;
  const sandboxExpiration = subscriptionType === "sandbox" ? new Date("2025-12-31T23:59:59") : null;

  // Get regular plus entitlement expiration date
  const regularExpiration = getEntitlementExpirationDate(appUser, "ultra");

  // Return the higher of the two dates
  if (sandboxExpiration && regularExpiration) {
    return sandboxExpiration.getTime() > regularExpiration.getTime() ? sandboxExpiration : regularExpiration;
  }

  // Return whichever one exists
  return sandboxExpiration || regularExpiration || null;
};

/**
 * Check if user is a sandbox user with temporary access
 * @param {Object} appUser - The app user object
 * @return {boolean} - True if user is a sandbox user
 */
export const isSandboxUser = (appUser: AppUserLike): boolean => {
  if (!appUser || !appUser.userInDb) {
    return false;
  }

  const user = appUser.userInDb;
  // Check both new structure (subscription.type) and legacy (subscriptionType)
  const subscriptionType = user.subscription?.type || user.subscriptionType;
  return subscriptionType === "sandbox";
};
